import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Leaf, Sprout } from 'lucide-react';
import { LegalFooter } from '@/components/LegalFooter';
import { motion } from 'framer-motion';
import gardenBgLogin from '@/assets/garden-bg-login.webp';

export default function Auth() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { signIn, signUp } = useAuth();

  if (loading) return (
    <div className="flex min-h-screen items-center justify-center bg-background botanical-dots">
      <Sprout className="h-8 w-8 animate-gentle-bounce text-primary" />
    </div>
  );
  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (isSignUp) {
        await signUp(email, password, displayName);
        toast.success('Account created! Check your email to confirm.');
      } else {
        await signIn(email, password);
        toast.success('Welcome back!');
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4 relative overflow-hidden">
      {/* Top nav bar */}
      <nav className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 py-3 sm:px-8">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl garden-gradient shadow-soft">
            <Leaf className="h-4 w-4 text-primary-foreground" />
          </div>
          <span className="text-sm font-bold text-foreground tracking-tight">Plan My Garden</span>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="rounded-xl text-xs" onClick={() => navigate('/pricing')}>
            Pricing
          </Button>
          <Button variant="ghost" size="sm" className="rounded-xl text-xs" onClick={() => navigate('/about')}>
            About
          </Button>
        </div>
      </nav>

      {/* Watercolour garden background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <img
          src={gardenBgLogin}
          alt=""
          className="absolute bottom-0 left-0 w-full h-auto opacity-70 object-cover object-bottom"
          style={{ minHeight: '40%' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-background/60 to-background" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md space-y-8 relative z-10"
      >
        <div className="text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
            className="mx-auto mb-5 flex h-18 w-18 items-center justify-center rounded-3xl garden-gradient shadow-lifted"
            style={{ width: 72, height: 72 }}
          >
            <Leaf className="h-9 w-9 text-primary-foreground" />
          </motion.div>
          <h1 className="text-4xl font-bold text-foreground tracking-tight">Plan My Garden</h1>
          <p className="mt-2 text-muted-foreground text-base">Your personal gardening assistant</p>
        </div>

        <Card className="card-organic border-border/40 shadow-medium">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl">{isSignUp ? 'Create Account' : 'Welcome Back'}</CardTitle>
            <CardDescription>
              {isSignUp ? 'Start planning your garden today' : 'Sign in to your garden planner'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {isSignUp && (
                <Input
                  placeholder="Your name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="h-11 rounded-xl bg-background/50 border-border/60 focus:border-primary/40"
                />
              )}
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 rounded-xl bg-background/50 border-border/60 focus:border-primary/40"
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="h-11 rounded-xl bg-background/50 border-border/60 focus:border-primary/40"
              />
              <Button type="submit" className="w-full h-11 rounded-xl garden-gradient shadow-soft text-base font-semibold transition-all hover:shadow-medium hover:scale-[1.01] active:scale-[0.99]" disabled={submitting}>
                {submitting ? 'Please wait...' : isSignUp ? 'Create Account' : 'Sign In'}
              </Button>
            </form>
            <div className="mt-5 text-center">
              <button
                type="button"
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                onClick={() => setIsSignUp(!isSignUp)}
              >
                {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Decorative bottom flourish */}
        <div className="flex justify-center gap-1.5 pt-2">
          {['🌱', '🌿', '🌸', '🌿', '🌱'].map((e, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 0.5, y: 0 }}
              transition={{ delay: 0.5 + i * 0.1 }}
              className="text-sm"
            >
              {e}
            </motion.span>
          ))}
        </div>
      </motion.div>
      <LegalFooter />
    </div>
  );
}
